import { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { atomWithQuery, queryClientAtom } from 'jotai-tanstack-query';
import { createContext } from 'react';

import { getMyTradingCompetitionInfoApi, getTradingCompetitionApi } from '@flipster/apis/v2/trading-competition';
import { CompetitionStatus, CompetitionStatusEnum, isRankingType } from '@flipster/apis/v2/trading-competition/types';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { luckyDrawBunja } from '../../contentful/lucky-draw/state';
import { authBunja } from '../../shared/auth/state';
import { competitionRankingsApiBunja } from './content/leaderboard/state';
import { CompetitionItemFragment } from './query.generated';

export const CompetitionIdContext = createContext<string>('');
export const CompetitionIdScope = createScopeFromContext(CompetitionIdContext);

export const CompetitionContentfulContext = createContext<CompetitionItemFragment | null>(null);
const CompetitionContentfulScope = createScopeFromContext(CompetitionContentfulContext);

export type EventTabValue = string;

export const competitionBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { competitionAtoms, competitionQueryKeyAtom, myCompetitionInfoQueryKey } = bunja.use(competitionApiBunja);
  const competitionContentful = bunja.use(CompetitionContentfulScope);
  const { competitionRankingsQueryKeyAtom } = bunja.use(competitionRankingsApiBunja);

  const { dataAtom: competitionAtom, errorAtom: competitionErrorAtom } = competitionAtoms;

  const isRegisteredAtom = atom((get) => Boolean(get(competitionAtom)?.participationTime));

  async function refetchCompetition() {
    const queryClient = store.get(queryClientAtom);
    const competitionRankingsQueryKey = store.get(competitionRankingsQueryKeyAtom);
    const competitionQueryKey = store.get(competitionQueryKeyAtom);

    const withLuckyDraw = competitionContentful?.eventTabsCollection?.items?.find((t) => t?.value === 'lucky-draw');
    const refetchQueries = [
      queryClient.refetchQueries({ queryKey: competitionQueryKey }),
      queryClient.refetchQueries({ queryKey: myCompetitionInfoQueryKey }),
      queryClient.refetchQueries({ queryKey: competitionRankingsQueryKey }),
      queryClient.refetchQueries({ queryKey: ['getTradingCompetitionsListApi'], type: 'active' }),
    ];
    if (withLuckyDraw)
      refetchQueries.push(queryClient.refetchQueries({ queryKey: ['getLuckyDrawApi'], type: 'active' }));

    return Promise.all(refetchQueries);
  }

  async function refetchMyCompetitionInfo() {
    const queryClient = store.get(queryClientAtom);
    return queryClient.refetchQueries({ queryKey: myCompetitionInfoQueryKey });
  }

  return {
    competitionAtom,
    competitionErrorAtom,
    isRegisteredAtom,
    competitionContentful,
    refetchCompetition,
    refetchMyCompetitionInfo,
  };
});
competitionBunja.debugLabel = 'competitionBunja';

// 탭 선택 상태만을 별도로 분리하여 순환 참조를 방지
export const competitionTabsBunja = bunja(() => {
  const competitionContentful = bunja.use(CompetitionContentfulScope);
  const eventTabs = competitionContentful?.eventTabsCollection?.items.filter(Boolean) ?? [];

  const selectedEventTabValueBaseAtom = atom<EventTabValue | undefined>(undefined);

  const selectedEventTabValueAtom = atom(
    (get) => {
      const explicitSelected = get(selectedEventTabValueBaseAtom);
      if (explicitSelected) return explicitSelected;
      return eventTabs?.[0]?.value ?? undefined;
    },
    (_, set, next: EventTabValue) => {
      set(selectedEventTabValueBaseAtom, next);
    },
  );

  const scrollTargetRefAtom = atom<HTMLElement | null>(null);

  return {
    eventTabs,
    selectedEventTabValueAtom,
    scrollTargetRefAtom,
  };
});
competitionTabsBunja.debugLabel = 'competitionTabsBunja';

export const competitionEventsBunja = bunja(() => {
  const competitionContentful = bunja.use(CompetitionContentfulScope);
  const { eventTabs, selectedEventTabValueAtom } = bunja.use(competitionTabsBunja);
  const { competitionStatusAtom } = bunja.use(competitionApiBunja);
  const { luckyDrawsAtom } = bunja.use(luckyDrawBunja);

  const selectedEventTabAtom = atom((get) => {
    const selectedEventTabValue = get(selectedEventTabValueAtom);
    return eventTabs?.find((item) => item.value === selectedEventTabValue);
  });

  const selectedEventTabRulesAtom = atom((get) => {
    const selectedEventTabValue = get(selectedEventTabValueAtom);
    return eventTabs?.find((item) => item.value === selectedEventTabValue)?.rules;
  });

  const competitionEventContentfulDataAtom = atom((get) => {
    const selectedEventTabValue = get(selectedEventTabValueAtom);
    return competitionContentful?.eventTabsCollection?.items.find((item) => item?.value === selectedEventTabValue);
  });

  // 각 이벤트 탭별 status 주입
  const eventTabsWithStatusAtom = atom((get) => {
    const competitionStatus = get(competitionStatusAtom);
    const luckyDraws = get(luckyDrawsAtom);

    return eventTabs.map((item) => {
      switch (item.value) {
        case 'volume':
        case 'pnl':
        case 'roi':
          return { ...item, available: competitionStatus !== 'FINISHED' };
        case 'x-mas-etc':
          return { ...item, available: competitionStatus !== 'FINISHED' };
        case 'lucky-draw':
          return { ...item, available: luckyDraws?.[0]?.luckyDrawEventStatus !== 'FINISHED' };
        default:
          return { ...item, available: false };
      }
    });
  });

  const availableEventCountAtom = atom((get) => {
    const eventTabsWithStatus = get(eventTabsWithStatusAtom);

    return eventTabsWithStatus.filter((item) => item?.available).length;
  });

  const onlyTcEvent = !eventTabs.some((t) => !isRankingType(t?.value));

  return {
    eventTabs,
    eventTabsWithStatusAtom,
    selectedEventTabAtom,
    selectedEventTabValueAtom,
    selectedEventTabRulesAtom,
    availableEventCountAtom,
    competitionEventContentfulDataAtom,
    onlyTcEvent,
  };
});
competitionEventsBunja.debugLabel = 'competitionEventsBunja';

export const myCompetitionInfoBunja = bunja(() => {
  const { myCompetitionInfoAtoms } = bunja.use(competitionApiBunja);
  const { dataAtom: myCompetitionInfoDataAtom, errorAtom: myCompetitionInfoErrorAtom } = myCompetitionInfoAtoms;

  return {
    myCompetitionInfoDataAtom,
    myCompetitionInfoErrorAtom,
  };
});
myCompetitionInfoBunja.debugLabel = 'myCompetitionInfoBunja';

export const competitionApiBunja = bunja(() => {
  const id = bunja.use(CompetitionIdScope);
  const { isCookieLoggedInAtom } = bunja.use(authBunja);

  const competitionQueryKeyAtom = atom((get) => ['getTradingCompetition', id, get(isCookieLoggedInAtom)]);
  const competitionAtoms = createAxiosQueryAtoms((get) => ({
    queryKey: get(competitionQueryKeyAtom),
    queryFn: () => getTradingCompetitionApi(id),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
  }));

  const myCompetitionInfoQueryKey = ['getMyTradingCompetitionInfo', id];
  const myCompetitionInfoAtoms = createAxiosQueryAtoms((get) => ({
    queryKey: myCompetitionInfoQueryKey,
    queryFn: () => getMyTradingCompetitionInfoApi(id),
    refetchOnMount: true,
    refetchInterval: 1000 * 60 * 5,
    enabled: get(isCookieLoggedInAtom),
  }));

  const competitionStatusBaseAtom = atom<CompetitionStatus | undefined>(undefined);
  const competitionStatusAtom = atom(
    (get) => {
      const competition = get(competitionAtoms.dataAtom);
      const competitionStatus = get(competitionStatusBaseAtom);
      if (competitionStatus) return competitionStatus;
      return competition?.competitionStatus ?? CompetitionStatusEnum.Upcoming;
    },
    (_, set, next: CompetitionStatus) => {
      set(competitionStatusBaseAtom, next);
    },
  );

  return {
    competitionAtoms,
    myCompetitionInfoAtoms,
    competitionStatusAtom,
    // query keys
    competitionQueryKeyAtom,
    myCompetitionInfoQueryKey,
  };
});
competitionApiBunja.debugLabel = 'competitionApiBunja';

export function createAxiosQueryAtoms<T>(...args: Parameters<typeof atomWithQuery<AxiosResponse<T>>>) {
  const queryAtom = atomWithQuery<AxiosResponse<T>>(...args);
  const dataAtom = atom((get) => get(queryAtom).data?.data);

  const errorAtom = atom((get) => get(queryAtom).error);
  return { queryAtom, dataAtom, errorAtom };
}
